import { Box, Dialog, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import { HEADING_FONT } from "../theme/fonts";
import { COLORS } from "../theme/tokens";
import BankAccountFormBody from "./BankAccountFormBody";

// Popup modal for creating / editing a bank account (spec §5 Forms & Modals). A
// blue gradient TOP HEADER (icon + title) sits above the shared
// BankAccountFormBody.
//
// Props:
//   open      — controls visibility.
//   onClose   — close without saving.
//   onSuccess — called after a successful save (receives { isEdit }).
//   editId    — null = create; an id = edit.
function BankAccountModal({ open, onClose, onSuccess, editId = null }) {
  const isEdit = editId != null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: "16px",
            background: "#fff",
            border: `1px solid ${COLORS.hairline}`,
            boxShadow: "0 24px 64px rgba(16, 24, 40, 0.18)",
            backgroundImage: "none",
            overflow: "hidden",
          },
        },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", maxHeight: "90vh" }}>
        {/* Blue gradient top header */}
        <Box
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            gap: 2,
            px: { xs: 2.5, md: 3.5 },
            py: 2.5,
            color: "#fff",
            flexShrink: 0,
            overflow: "hidden",
            background: `linear-gradient(135deg, ${COLORS.primaryDark} 0%, ${COLORS.primary} 100%)`,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: -50,
              right: -30,
              width: 160,
              height: 160,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.12)",
              filter: "blur(40px)",
            }}
          />
          <Box
            sx={{
              width: 50,
              height: 50,
              borderRadius: "14px",
              background: "rgba(255,255,255,0.18)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              position: "relative",
              zIndex: 1,
            }}
          >
            <AccountBalanceOutlinedIcon sx={{ fontSize: 26 }} />
          </Box>
          <Box sx={{ minWidth: 0, position: "relative", zIndex: 1 }}>
            <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "1.5px", textTransform: "uppercase", opacity: 0.85 }}>
              {isEdit ? "Edit" : "New"}
            </Typography>
            <Typography sx={{ fontFamily: HEADING_FONT, fontSize: 22, fontWeight: 700, letterSpacing: "-0.4px", lineHeight: 1.15 }}>
              {isEdit ? "Edit Bank Account" : "Add Bank Account"}
            </Typography>
          </Box>

          <IconButton
            onClick={onClose}
            aria-label="Close"
            sx={{
              ml: "auto",
              position: "relative",
              zIndex: 1,
              color: "#fff",
              background: "rgba(255,255,255,0.16)",
              "&:hover": { background: "rgba(255,255,255,0.28)" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Form body (scrolls if taller than the viewport) */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            p: { xs: 2, md: 3 },
            background: COLORS.canvas,
          }}
        >
          <BankAccountFormBody
            key={editId ?? "new"}
            accountId={editId}
            onSuccess={onSuccess}
            onCancel={onClose}
          />
        </Box>
      </Box>
    </Dialog>
  );
}

export default BankAccountModal;
